import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import Header from './components/Header';
import ContentPage from './pages/ContentPage';
import Hero from './components/Hero';
import Stats from './components/Stats';
import TrustBar from './components/TrustBar';
import Services from './components/Services';
import ProcessFlow from './components/ProcessFlow';
import Projects from './components/Projects';
import Marquee from './components/Marquee';
import Testimonials from './components/Testimonials';
import FAQ from './components/FAQ';
import About from './components/About';
import ContactCTA from './components/ContactCTA';
import Footer from './components/Footer';
import ScrollProgress from './components/ScrollProgress';
import CursorRing from './components/CursorRing';
import IntroOverlay from './components/IntroOverlay';
import FloatingContact from './components/FloatingContact';
import NotFound from './components/NotFound';
import Resume from './components/Resume';
import { faqs, navLinks, processSteps, projects, services, socials, testimonials } from './data/portfolioData';

function App() {
  const [resumeOpen, setResumeOpen] = useState(false);

  useEffect(() => {
    const tabTitle = 'Nithin Choudary | Freelance Full Stack Web Developer Hyderabad';
    const seoTitle =
      'Nithin Sai Choudray | Freelance Full Stack Web Developer & Website Designer in Hyderabad';
    const seoDescription =
      'Nithin Sai Choudray — SDE at KOKO AI (EdTech) and freelance full stack web developer & website designer in Hyderabad, India. React, Node.js, AI integration, premium UI design, and end-to-end website development for startups and businesses worldwide.';
    const keywords = [
      'freelance web developer Hyderabad',
      'freelance full stack developer India',
      'hire React developer Hyderabad',
      'website designer Hyderabad',
      'Node.js developer for hire',
      'MERN stack freelancer',
      'AI integration developer',
      'OpenAI integration freelancer',
      'premium website design India',
      'small business website developer',
      'startup website developer',
      'landing page design freelancer',
      'web app developer Hyderabad',
      'remote freelance developer India',
      'Nithin Choudary',
      'Nithin Sai Choudray',
      'portfolio website developer'
    ].join(', ');
    const pageUrl = `${window.location.origin}/`;
    const imageUrl = `${window.location.origin}/nithin-og.jpg`;

    const setMeta = (attribute, key, content) => {
      let element = document.head.querySelector(`meta[${attribute}="${key}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    const setCanonical = (href) => {
      let link = document.head.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };

    document.title = tabTitle;
    setMeta('name', 'description', seoDescription);
    setMeta('name', 'keywords', keywords);
    setMeta('name', 'author', 'Nithin Sai Choudray');
    setMeta('property', 'og:title', seoTitle);
    setMeta('property', 'og:description', seoDescription);
    setMeta('property', 'og:url', pageUrl);
    setMeta('property', 'og:image', imageUrl);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:locale', 'en_IN');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', seoTitle);
    setMeta('name', 'twitter:description', seoDescription);
    setMeta('name', 'twitter:image', imageUrl);
    setCanonical(pageUrl);

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Person',
          '@id': `${pageUrl}#person`,
          name: 'Nithin Sai Choudray',
          alternateName: ['Nithin Choudray', 'Nithin Sai Choudary', 'Nithin Jampala'],
          jobTitle: 'Software Development Engineer at KOKO AI & Freelance Full Stack Web Developer',
          worksFor: {
            '@type': 'Organization',
            name: 'KOKO AI',
            industry: 'EdTech'
          },
          description:
            'Software Development Engineer at KOKO AI (EdTech startup) and freelance full stack web developer and website designer based in Hyderabad, India. Specializes in premium React experiences, Node.js backends, and AI integrations. Building a community for developers and designers to learn and help each other.',
          knowsAbout: [
            'React',
            'Node.js',
            'Full Stack Web Development',
            'Website Design',
            'AI API Integration',
            'Tailwind CSS',
            'MongoDB',
            'PostgreSQL'
          ],
          url: pageUrl,
          image: imageUrl,
          email: 'mailto:nithinsai2603@gmail.com',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Hyderabad',
            addressRegion: 'Telangana',
            addressCountry: 'IN'
          },
          sameAs: socials.map((social) => social.href)
        },
        {
          '@type': 'ProfessionalService',
          '@id': `${pageUrl}#service`,
          name: 'Nithin Sai Choudray - Freelance Web Development & Design',
          provider: { '@id': `${pageUrl}#person` },
          areaServed: ['India', 'Hyderabad', 'Worldwide'],
          serviceType: [
            'Freelance Full Stack Web Development',
            'Website Design',
            'Website Maintenance',
            'AI API Integration',
            'React Development',
            'Node.js Backend Development'
          ],
          url: pageUrl,
          priceRange: '$$',
          hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'Freelance Services',
            itemListElement: services.map((s) => ({
              '@type': 'Offer',
              itemOffered: { '@type': 'Service', name: s.title, description: s.description }
            }))
          }
        },
        {
          '@type': 'WebSite',
          '@id': `${pageUrl}#website`,
          url: pageUrl,
          name: 'Nithin Sai Choudray Portfolio',
          publisher: { '@id': `${pageUrl}#person` }
        },
        {
          '@type': 'FAQPage',
          '@id': `${pageUrl}#faq`,
          mainEntity: faqs.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a }
          }))
        }
      ]
    };

    let schemaScript = document.head.querySelector('#ld-json-person');
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.setAttribute('type', 'application/ld+json');
      schemaScript.setAttribute('id', 'ld-json-person');
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify(schema);

    AOS.init({
      duration: 720,
      easing: 'ease-out-cubic',
      once: true,
      offset: 32
    });
  }, []);

  const portfolio = (
    <div className="min-h-screen overflow-x-clip bg-paper text-ink">
      <IntroOverlay />
      <CursorRing />
      <ScrollProgress />
      <Header navLinks={navLinks} onOpenResume={() => setResumeOpen(true)} />
      <main id="main-content" aria-label="Portfolio Content">
        <Hero />
        <Stats />
        <Services services={services} />
        <ProcessFlow steps={processSteps} />
        <Projects projects={projects} />
        <TrustBar />
        <Marquee />
        <Testimonials testimonials={testimonials} />
        <About />
        <FAQ faqs={faqs} />
        <ContactCTA socials={socials} />
      </main>
      <Footer socials={socials} />
      <FloatingContact />
      <Resume open={resumeOpen} onClose={() => setResumeOpen(false)} />
    </div>
  );

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={portfolio} />
        <Route path="/index.html" element={portfolio} />
        <Route path="/content" element={<ContentPage />} />
        <Route path="*" element={<div className="min-h-screen bg-paper text-ink"><NotFound /></div>} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
